import React from "react";
import { Box, Typography, Button, Paper } from "@mui/material";
import { ErrorOutline, Refresh } from "@mui/icons-material";
import { isChunkLoadError, reloadForStaleChunk } from "../../utils/chunkLoadRecovery";

/**
 * Error Boundary to catch React runtime errors and show a fallback UI
 * instead of the generic "Script error" overlay.
 * Handles cross-origin script errors (e.g. Google Maps) gracefully.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("[ErrorBoundary] Caught error:", error, errorInfo);
    if (isChunkLoadError(error)) {
      reloadForStaleChunk(error);
    }
  }

  handleRetry = () => {
    if (isChunkLoadError(this.state.error)) {
      window.location.reload();
      return;
    }
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }
      const isScriptError = this.state.error?.message === "Script error.";
      const isChunkError = isChunkLoadError(this.state.error);
      return (
        <Box
          sx={{
            minHeight: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: 2,
            bgcolor: "background.default",
          }}
        >
          <Paper
            elevation={2}
            sx={{
              p: 4,
              maxWidth: 480,
              textAlign: "center",
            }}
          >
            <ErrorOutline sx={{ fontSize: 64, color: "error.main", mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              Something went wrong
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              {isChunkError
                ? "A new version of the app was deployed. Please reload the page to continue."
                : isScriptError
                ? "A script error occurred. This can happen when loading external services (e.g. Google Maps). Try refreshing the page or disabling browser extensions."
                : this.state.error?.message || "An unexpected error occurred."}
            </Typography>
            <Button
              variant="contained"
              startIcon={<Refresh />}
              onClick={this.handleRetry}
              sx={{ mt: 1 }}
            >
              {isChunkError ? "Reload page" : "Try again"}
            </Button>
          </Paper>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
